import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Cascader,
  Col,
  Descriptions,
  Form,
  Input,
  message,
  Popconfirm,
  Radio,
  Row,
  Space,
  Table,
  Tag,
  Typography
} from 'antd';
import { LinkOutlined, ReloadOutlined, SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import { schoolAffiliationCorrectionAPI } from '../../services/api';
import OfficialSchoolPicker from '../../components/schools/OfficialSchoolPicker';
import { areaData } from '../../utils/areaData';
import { formatAreaText, getAreaLabelsByCodes } from '../../utils/areaHelpers';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const STATUS_CONFIG = {
  pending: { color: 'processing', label: '待审核' },
  approved: { color: 'success', label: '已通过' },
  rejected: { color: 'error', label: '已驳回' },
  cancelled: { color: 'default', label: '已取消' }
};

const SchoolAffiliationCorrectionSubmit = () => {
  const { user } = useAuth();
  const [manualForm] = Form.useForm();
  const [inviteForm] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [joinLoading, setJoinLoading] = useState(false);
  const [officialJoinLoading, setOfficialJoinLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [selectedOfficialSchool, setSelectedOfficialSchool] = useState(null);
  const [officialPreview, setOfficialPreview] = useState(null);
  const [officialPreviewLoading, setOfficialPreviewLoading] = useState(false);
  const [migrationMode, setMigrationMode] = useState('none');
  const [selectedClassIds, setSelectedClassIds] = useState([]);

  const fetchMyRecords = async () => {
    try {
      setListLoading(true);
      const response = await schoolAffiliationCorrectionAPI.getMyList({ limit: 50 });
      if (response.data.success) {
        setRecords(response.data.data || []);
      }
    } catch (error) {
      message.error('获取我的入校记录失败');
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchMyRecords();
  }, []);

  useEffect(() => {
    let active = true;
    const loadPreview = async () => {
      const code = selectedOfficialSchool?.officialSchoolCode;
      if (!code) {
        setOfficialPreview(null);
        setSelectedClassIds([]);
        setMigrationMode('none');
        return;
      }
      try {
        setOfficialPreviewLoading(true);
        setMigrationMode('none');
        const response = await schoolAffiliationCorrectionAPI.getOfficialMigrationPreview(code);
        if (!active || !response.data.success) return;
        const preview = response.data.data || null;
        setOfficialPreview(preview);
        const duplicateIds = new Set(
          (preview?.duplicateClasses || []).map((item) => String(item.sourceClassId || item.source_class_id))
        );
        setSelectedClassIds(
          (preview?.classes || [])
            .filter((item) => item.migrationEligible !== false && !duplicateIds.has(String(item.id)))
            .map((item) => String(item.id))
        );
      } catch (error) {
        if (active) {
          setOfficialPreview(null);
          setSelectedClassIds([]);
          message.warning(error.response?.data?.message || '班级迁移预览加载失败，可先选择仅加入学校');
        }
      } finally {
        if (active) setOfficialPreviewLoading(false);
      }
    };
    loadPreview();
    return () => { active = false; };
  }, [selectedOfficialSchool?.officialSchoolCode]);

  const handleJoinByCode = async (values) => {
    try {
      setJoinLoading(true);
      const response = await schoolAffiliationCorrectionAPI.joinByCode({
        invitation_code: values.invitation_code
      });

      if (response.data.success) {
        message.success(response.data.message || '已提交入校申请，等待管理员审核');
        inviteForm.resetFields();
        fetchMyRecords();
      } else {
        message.error(response.data.message || '学校邀请码申请失败');
      }
    } catch (error) {
      message.error(error.response?.data?.message || '学校邀请码申请失败');
    } finally {
      setJoinLoading(false);
    }
  };

  const handleSubmitApplication = async (values) => {
    try {
      setSubmitLoading(true);
      const { area_codes, ...restValues } = values;
      const { province, city, county } = getAreaLabelsByCodes(area_codes);

      const response = await schoolAffiliationCorrectionAPI.submit({
        ...restValues,
        requested_province: province || null,
        requested_city: city || null,
        requested_county: county || null
      });

      if (response.data.success) {
        message.success(response.data.message || '入校申请已提交');
        manualForm.resetFields();
        fetchMyRecords();
      } else {
        message.error(response.data.message || '入校申请提交失败');
      }
    } catch (error) {
      message.error(error.response?.data?.message || '入校申请提交失败');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleJoinOfficialSchool = async () => {
    if (!selectedOfficialSchool?.officialSchoolCode) {
      message.warning('请先按省、市、区县和学校类型选择标准学校');
      return;
    }
    if (migrationMode === 'move_teacher_classes' && selectedClassIds.length === 0) {
      message.warning('请选择至少一个可迁移班级，或改为仅加入学校');
      return;
    }
    try {
      setOfficialJoinLoading(true);
      const response = await schoolAffiliationCorrectionAPI.joinOfficialSchool(
        selectedOfficialSchool.officialSchoolCode,
        {
          migrationMode,
          classIds: migrationMode === 'move_teacher_classes' ? selectedClassIds : []
        }
      );
      if (response.data.success) {
        const data = response.data.data || {};
        if (data.status === 'approved') {
          const moved = Number(data.migration_result?.classes || 0);
          message.success(data.auto_approved
            ? `目标学校暂无管理员，已自动通过${moved > 0 ? `并迁移 ${moved} 个班级` : ''}`
            : '您已加入该标准学校');
        } else {
          message.info('目标学校已有管理员，申请已提交审核；审核通过前不会迁移数据');
        }
        setSelectedOfficialSchool(null);
        setOfficialPreview(null);
        setSelectedClassIds([]);
        setMigrationMode('none');
        fetchMyRecords();
      } else {
        message.error(response.data.message || '标准学校申请失败');
      }
    } catch (error) {
      message.error(error.response?.data?.message || '标准学校申请失败');
    } finally {
      setOfficialJoinLoading(false);
    }
  };

  const handleCancel = async (id) => {
    try {
      const response = await schoolAffiliationCorrectionAPI.cancel(id);
      if (response.data.success) {
        message.success('申请已取消');
        fetchMyRecords();
      } else {
        message.error(response.data.message || '取消申请失败');
      }
    } catch (error) {
      message.error(error.response?.data?.message || '取消申请失败');
    }
  };

  const columns = [
    {
      title: '提交时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-')
    },
    {
      title: '当前学校',
      key: 'current_school_name',
      render: (_, record) => record.current_school_name || '-'
    },
    {
      title: '目标学校',
      dataIndex: 'requested_school_name',
      key: 'requested_school_name'
    },
    {
      title: '目标地区',
      key: 'requested_region',
      render: (_, record) =>
        formatAreaText(record.requested_province, record.requested_city, record.requested_county) || '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => {
        const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
        return <Tag color={config.color}>{config.label}</Tag>;
      }
    },
    {
      title: '审核备注',
      dataIndex: 'review_note',
      key: 'review_note',
      render: (value) => value || <Text type="secondary">-</Text>
    },
    {
      title: '数据处理',
      key: 'migration_result',
      width: 150,
      render: (_, record) => {
        const moved = Number(record.migration_result?.classes || 0);
        if (moved > 0) return <Tag color="blue">已迁移 {moved} 个班级</Tag>;
        if (record.migration_mode === 'move_teacher_classes' && record.status === 'pending') {
          return <Tag color="gold">待审核迁移</Tag>;
        }
        return <Text type="secondary">仅加入学校</Text>;
      }
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        record.status === 'pending' ? (
          <Popconfirm
            title="确认取消这条入校申请吗？"
            onConfirm={() => handleCancel(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button type="link" danger size="small">取消申请</Button>
          </Popconfirm>
        ) : (
          <Text type="secondary">-</Text>
        )
      )
    }
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Card>
        <Title level={4} style={{ marginTop: 0, marginBottom: 8 }}>
          学校入校申请
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          老师可以输入学校邀请码快速提交入校申请；如果没有邀请码，也可以提交人工申请，等待学校管理员审核。
        </Paragraph>
      </Card>

      <Card>
        <Alert
          type="info"
          showIcon
          message="当前账号信息"
          description={
            <>
              <div>当前账号：{user?.name || user?.username || '-'}</div>
              <div>当前角色：{user?.role || user?.userType || '-'}</div>
              <div>当前学校ID：{user?.schoolId || '-'}</div>
            </>
          }
          style={{ marginBottom: 24 }}
        />

        <Row gutter={[16, 16]}>
          <Col xs={24} xl={8}>
            <Card title="学校邀请码入校" type="inner" style={{ height: '100%' }}>
              <Paragraph type="secondary">
                如果学校管理员已经发给您学校二维码或邀请码，扫码后提交的邀请码会直接进入该学校的待审核列表。
              </Paragraph>
              <Form form={inviteForm} layout="vertical" onFinish={handleJoinByCode}>
                <Form.Item
                  name="invitation_code"
                  label="学校邀请码"
                  rules={[{ required: true, message: '请输入学校邀请码' }]}
                >
                  <Input prefix={<LinkOutlined />} placeholder="请输入学校邀请码" maxLength={12} />
                </Form.Item>
                <Form.Item style={{ marginBottom: 0 }}>
                  <Button type="primary" htmlType="submit" loading={joinLoading} icon={<SendOutlined />}>
                    提交入校申请
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </Col>

          <Col xs={24} xl={16}>
            <Card title="从全国标准学校库申请加入" type="inner">
              <Paragraph type="secondary">
                请依次选择省、市、区县和学校类型。学校有管理员时需要审核；没有管理员时系统自动通过，普通教师仍不会获得全校管理权限。
              </Paragraph>
              <OfficialSchoolPicker value={selectedOfficialSchool} onChange={setSelectedOfficialSchool} disabled={officialJoinLoading} />
              {selectedOfficialSchool && (
                <Card size="small" loading={officialPreviewLoading} style={{ marginTop: 16 }} title="加入方式与数据携带">
                  {officialPreview?.approvalPolicy && (
                    <Alert
                      type={officialPreview.approvalPolicy.hasAdministrator ? 'warning' : 'success'}
                      showIcon
                      style={{ marginBottom: 16 }}
                      message={officialPreview.approvalPolicy.hasAdministrator
                        ? `该学校已有 ${officialPreview.approvalPolicy.administratorCount} 名管理员，提交后等待审核`
                        : '该学校暂无管理员，提交后自动通过'}
                      description="无论是否自动通过，普通教师都只能访问自己参与的班级数据。"
                    />
                  )}
                  <Radio.Group value={migrationMode} onChange={(event) => setMigrationMode(event.target.value)}>
                    <Space direction="vertical">
                      <Radio value="none">仅加入标准学校，保留原学校和原班级归属</Radio>
                      <Radio value="move_teacher_classes" disabled={!officialPreview || selectedClassIds.length === 0}>
                        同时携带我选择的班级及关联数据
                      </Radio>
                    </Space>
                  </Radio.Group>
                  {officialPreview && (
                    <>
                      <Descriptions size="small" column={3} style={{ marginTop: 16, marginBottom: 12 }}>
                        <Descriptions.Item label="当前班级">{officialPreview.totals?.classes || 0}</Descriptions.Item>
                        <Descriptions.Item label="可安全迁移">{officialPreview.totals?.migrationEligibleClasses || 0}</Descriptions.Item>
                        <Descriptions.Item label="涉及学生">{officialPreview.totals?.students || 0}</Descriptions.Item>
                      </Descriptions>
                      <Table
                        rowKey={(record) => String(record.id)}
                        size="small"
                        pagination={false}
                        dataSource={officialPreview.classes || []}
                        rowSelection={migrationMode === 'move_teacher_classes' ? {
                          selectedRowKeys: selectedClassIds,
                          onChange: (keys) => setSelectedClassIds(keys.map(String)),
                          getCheckboxProps: (record) => {
                            const duplicate = (officialPreview.duplicateClasses || []).some(
                              (item) => String(item.sourceClassId || item.source_class_id) === String(record.id)
                            );
                            return { disabled: record.migrationEligible === false || duplicate };
                          }
                        } : undefined}
                        columns={[
                          { title: '班级', dataIndex: 'name', key: 'name' },
                          { title: '当前学校', dataIndex: 'schoolName', key: 'schoolName' },
                          { title: '学生', dataIndex: 'studentCount', key: 'studentCount', width: 70 },
                          {
                            title: '迁移状态',
                            key: 'eligibility',
                            width: 130,
                            render: (_, record) => record.migrationEligible === false
                              ? <Tag color="orange">多人共管，需另行处理</Tag>
                              : <Tag color="green">可迁移</Tag>
                          }
                        ]}
                      />
                    </>
                  )}
                </Card>
              )}
              <Space style={{ marginTop: 16 }}>
                <Button type="primary" onClick={handleJoinOfficialSchool} loading={officialJoinLoading} disabled={!selectedOfficialSchool || officialPreviewLoading} icon={<SendOutlined />}>
                  申请加入标准学校
                </Button>
                {selectedOfficialSchool && <Text type="secondary">已选：{selectedOfficialSchool.name}</Text>}
              </Space>
            </Card>
          </Col>
          <Col span={24}>
            <Card title="学校不在标准库时的人工申请" type="inner">
              <Form form={manualForm} layout="vertical" onFinish={handleSubmitApplication}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="requested_school_name"
                      label="目标学校名称"
                      rules={[{ required: true, message: '请填写目标学校名称' }]}
                    >
                      <Input placeholder="例如：平远县田家炳中学" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="area_codes"
                      label="目标学校省市县"
                      rules={[{ required: true, message: '请选择目标学校省市县' }]}
                    >
                      <Cascader options={areaData} placeholder="请选择省/市/县" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="requested_address" label="详细地址">
                      <Input placeholder="可补充街道、乡镇、校区等信息" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="contact_phone" label="联系电话">
                      <Input placeholder="便于管理员核实，选填" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="reason" label="申请说明">
                  <TextArea
                    rows={4}
                    placeholder="例如：当前学校挂错了，希望并入规范学校；或学校刚开通，需要人工核准归属。"
                    maxLength={300}
                    showCount
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button type="primary" htmlType="submit" loading={submitLoading}>
                    提交人工申请
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </Col>
        </Row>
      </Card>

      <Card
        title="我的入校记录"
        extra={
          <Button icon={<ReloadOutlined />} onClick={fetchMyRecords} loading={listLoading}>
            刷新
          </Button>
        }
      >
        <Table
          rowKey="id"
          loading={listLoading}
          columns={columns}
          dataSource={records}
          pagination={{ pageSize: 10, showSizeChanger: true }}
        />
      </Card>
    </Space>
  );
};

export default SchoolAffiliationCorrectionSubmit;
